import { ActionIcon, Box, Group, Menu, Paper, SegmentedControl, Stack, Table as MantineTable, Text, TextInput, Tooltip, UnstyledButton, Card, Divider } from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconArrowsSort, IconChevronDown, IconChevronRight, IconEyeOff, IconPinned, IconSearch, IconSitemap, IconSortAscending, IconSortDescending } from "@tabler/icons-react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { AppTableFeatures } from "./useAppTable";
import type { Cell, Column, Header, ReactTable, Row, RowData } from "@tanstack/react-table";
import { NameSortMenuItems } from "./NameSortMenuItems";
import { AppTableToolbarControls } from "./AppTableToolbarControls";
import { renderAppTableCell } from "./renderAppTableCell";
import { useStickyTableHeaderHeight } from "@hooks/useStickyTableHeaderHeight";
import "./app-data-table.css";

const DEFAULT_TOOLBAR_OPTIONS = {
  search: true,
  filter: true,
  grouping: false,
  sort: true,
  view: true,
  moreOptions: true,
} as const;

export type AppDataTableView<TData extends RowData> = {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Omitting render uses the standard table renderer. */
  render?: (context: {
    table: ReactTable<AppTableFeatures, TData, any>;
    rows: Array<Row<AppTableFeatures, TData>>;
  }) => ReactNode;
};

export type AppDataTableProps<TData extends RowData> = {
  table: ReactTable<AppTableFeatures, TData, any>;
  tableKey: string;
  gap?: ComponentProps<typeof Group>["gap"];
  toolbarPadding?: ComponentProps<typeof Group>["p"];
  tableProps?: Omit<ComponentProps<typeof MantineTable>, "children" | "style"> & {
    style?: React.CSSProperties;
  };
  toolbar?: ReactNode;
  toolbarActions?: ReactNode;
  body?: ReactNode;
  views?: readonly AppDataTableView<TData>[];
  defaultViewId?: string;
  viewId?: string;
  onViewChange?: (viewId: string) => void;
  toolbarSlots?: {
    start?: ReactNode;
    middle?: ReactNode;
    end?: ReactNode;
  };
  toolbarOptions?: {
    search?: boolean;
    filter?: boolean;
    grouping?: boolean;
    sort?: boolean;
    view?: boolean;
    moreOptions?: boolean;
    searchWidth?: number | string;
  };
  emptyState?: ReactNode;
  loading?: boolean;
  loadingState?: ReactNode;
  error?: ReactNode;
  onRowClick?: (row: TData) => void;
  onRowContextMenu?: (event: React.MouseEvent, row: TData) => void;
  onBodyContextMenu?: (event: React.MouseEvent) => void;
  rowDraggable?: (row: TData) => boolean;
  onRowDragStart?: (event: React.DragEvent, row: TData) => void;
  rowStyle?: (row: TData) => React.CSSProperties | undefined;
  renderBeforeRow?: (row: Row<AppTableFeatures, TData>, index: number, rows: Array<Row<AppTableFeatures, TData>>) => ReactNode;
  virtualization?: {
    enabled?: boolean;
    estimateSize?: number;
    overscan?: number;
  };
  withCard?: boolean;
  minWidth?: number;
  maxHeight?: number | string;
  hideHeaderControls?: boolean;
  scrollbarType?: "auto" | "always" | "scroll" | "hover" | "never";
};

function getHeaderLabel<TData extends RowData>(column: Column<AppTableFeatures, TData, unknown>) {
  const metaLabel = column.columnDef.meta?.label;
  if (metaLabel) return metaLabel;
  const header = column.columnDef.header;
  return typeof header === "string" ? header : column.id;
}

function sortDirection<TData extends RowData>(column: Column<AppTableFeatures, TData, unknown>) {
  const sorted = column.getIsSorted();
  return sorted === "asc" || sorted === "desc" ? sorted : undefined;
}

function SortIcon({ direction }: { direction: "asc" | "desc" | undefined }) {
  if (direction === "asc") return <IconSortAscending color="var(--mantine-color-dimmed)" size={16} />;
  if (direction === "desc") return <IconSortDescending color="var(--mantine-color-dimmed)" size={16} />;
  return <IconArrowsSort color="var(--mantine-color-dimmed)" className="app-data-table-sort-icon-unsorted" size={16} />;
}

function HeaderContent<TData extends RowData>({
  header,
  table,
  title,
}: {
  header: Header<AppTableFeatures, TData, unknown>;
  table: ReactTable<AppTableFeatures, TData, any>;
  title: string;
}) {
  const headerTooltip = header.column.columnDef.meta?.headerTooltip;

  return typeof header.column.columnDef.header === "string"
    ? (
      headerTooltip ? (
        <Tooltip label={headerTooltip} withArrow>
          <Text fw={600} size="xs" truncate>{title}</Text>
        </Tooltip>
      ) : <Text fw={600} size="xs" truncate>{title}</Text>
    )
    : <table.FlexRender header={header} />;
}

function getPinningStyles<TData extends RowData>(
  column: Column<AppTableFeatures, TData, unknown>,
  selected = false,
  isHeader = false,
): React.CSSProperties {
  const pinned = column.getIsPinned();
  const isLastLeft = pinned === "left" && column.getIsLastColumn("left");
  const isFirstRight = pinned === "right" && column.getIsFirstColumn("right");
  const sideShadow = isLastLeft
    ? "-4px 0 4px -4px var(--mantine-color-default-border) inset"
    : isFirstRight
      ? "4px 0 4px -4px var(--mantine-color-default-border) inset"
      : undefined;
  const headerBottomShadow = isHeader
    ? "0 -1px 0 0 var(--mantine-color-default-border) inset"
    : undefined;
  const boxShadow = [sideShadow, headerBottomShadow].filter(Boolean).join(", ") || undefined;

  return {
    boxShadow,
    left: pinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: pinned === "right" ? `${column.getAfter("right")}px` : undefined,
    position: pinned ? "sticky" : "relative",
    borderRight: isLastLeft ? "1px solid var(--mantine-color-default-border)" : undefined,
    borderLeft: isFirstRight ? "1px solid var(--mantine-color-default-border)" : undefined,
    background: selected ? "var(--mantine-color-body)" : undefined,
    zIndex: pinned ? 2 : 0,
  };
}

export function AppDataTableHeaderCell<TData extends RowData>({
  header,
  table,
  hideControls = false,
}: {
  header: Header<AppTableFeatures, TData, unknown>;
  table: ReactTable<AppTableFeatures, TData, any>;
  hideControls?: boolean;
}) {
  const column = header.column;
  const direction = sortDirection(column);
  const title = getHeaderLabel(column);
  const nameSorting = column.columnDef.meta?.nameSorting;
  const headerAlign = column.columnDef.meta?.align ?? "left";
  const headerJustify = headerAlign === "center"
    ? "center"
    : headerAlign === "right"
      ? "flex-end"
      : "flex-start";
  const canShowMenu = column.id !== "select"
    && !hideControls
    && !column.columnDef.meta?.hideHeaderControl
    && (column.getCanSort() || column.getCanGroup() || column.getCanHide() || column.getCanPin());

  return (
    <MantineTable.Th
      colSpan={header.colSpan}
      aria-sort={direction === "asc" ? "ascending" : direction === "desc" ? "descending" : "none"}
      data-sort={direction}
      className={column.getIsPinned() ? "app-data-table-sticky-cell" : undefined}
      style={{
        width: `calc(var(--header-${header.id}-size, ${header.getSize()}) * 1px)`,
        padding: column.columnDef.meta?.compact ? 2 : 8,
        ...getPinningStyles(column, false, true),
      }}
      //bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))"
    >
      <Box style={{ position: "relative", paddingRight: column.getCanResize() ? 8 : 0 }}>
        {header.isPlaceholder ? null : (
          <Group gap={4} wrap="nowrap" justify={headerJustify} style={{ width: "100%" }}>
            {column.getCanSort() && !hideControls ? (
              <UnstyledButton
                onClick={column.getToggleSortingHandler()}
                className="app-data-table-sort-trigger"
                style={{ minWidth: 0 }}
              >
                <Group gap={4} wrap="nowrap">
                  <HeaderContent header={header} table={table} title={title} />
                  <SortIcon direction={direction} />
                </Group>
              </UnstyledButton>
            ) : (
              <HeaderContent header={header} table={table} title={title} />
            )}
            {canShowMenu ? (
              <Menu shadow="md" width={180}>
                <Menu.Target>
                  <ActionIcon variant="subtle" size="xs" aria-label={`Open ${title} column menu`}>
                    <IconChevronDown size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{title}</Menu.Label>
                  {column.getCanSort() ? (
                    <>
                      {nameSorting ? (
                        <NameSortMenuItems
                          currentMode={nameSorting.mode}
                          showCheck={Boolean(direction)}
                          onModeChange={nameSorting.onModeChange}
                        />
                      ) : (
                        <>
                          <Menu.Item leftSection={<IconSortAscending size={16} />} onClick={() => column.toggleSorting(false)}>
                            Sort ascending
                          </Menu.Item>
                          <Menu.Item leftSection={<IconSortDescending size={16} />} onClick={() => column.toggleSorting(true)}>
                            Sort descending
                          </Menu.Item>
                        </>
                      )}
                    </>
                  ) : null}
                  {column.getCanGroup() ? (
                    <>
                      <Menu.Divider />
                      <Menu.Item leftSection={<IconSitemap size={16} />} onClick={column.getToggleGroupingHandler()}>
                        {column.getIsGrouped() ? `Ungroup ${title.toLowerCase()}` : `Group by ${title.toLowerCase()}`}
                      </Menu.Item>
                    </>
                  ) : null}
                  {column.getCanPin() ? (
                    <>
                      <Menu.Divider />
                      <Menu.Item leftSection={<IconPinned size={16} />} disabled={column.getIsPinned() === "left"} onClick={() => column.pin("left")}>
                        Pin left
                      </Menu.Item>
                      <Menu.Item leftSection={<IconPinned size={16} />} disabled={column.getIsPinned() === "right"} onClick={() => column.pin("right")}>
                        Pin right
                      </Menu.Item>
                      {column.getIsPinned() ? (
                        <Menu.Item leftSection={<IconPinned size={16} opacity={0.45} />} onClick={() => column.pin(false)}>
                          Unpin
                        </Menu.Item>
                      ) : null}
                    </>
                  ) : null}
                  {column.getCanHide() ? (
                    <>
                      <Menu.Divider />
                      <Menu.Item leftSection={<IconEyeOff size={16} />} onClick={() => column.toggleVisibility(false)}>
                        Hide
                      </Menu.Item>
                    </>
                  ) : null}
                </Menu.Dropdown>
              </Menu>
            ) : null}
          </Group>
        )}
        {column.getCanResize() && !hideControls ? (
          <Box
            onDoubleClick={() => column.resetSize()}
            onMouseDown={header.getResizeHandler()}
            onTouchStart={header.getResizeHandler()}
            style={{
              position: "absolute",
              top: 0,
              right: -6,
              width: 6,
              height: "100%",
              cursor: "col-resize",
              touchAction: "none",
              background: column.getIsResizing() ? "var(--mantine-primary-color-filled)" : "transparent",
            }}
          />
        ) : null}
      </Box>
    </MantineTable.Th>
  );
}

function renderCell<TData extends RowData>(table: ReactTable<AppTableFeatures, TData, any>, cell: Cell<AppTableFeatures, TData, unknown>) {
  return renderAppTableCell(table, cell);
}

function getGroupedRowLabel<TData extends RowData>(cell: Cell<AppTableFeatures, TData, unknown>) {
  const value = cell.getValue();
  if (value === null || value === undefined || value === "") return "Empty";
  return String(value);
}

function getGroupedRowCount<TData extends RowData>(row: Row<AppTableFeatures, TData>) {
  return row.getLeafRows().filter((leafRow) => !leafRow.getIsGrouped()).length;
}

export function AppDataTable<TData extends RowData>({
  table,
  tableKey,
  gap = "xs",
  toolbarPadding,
  tableProps,
  toolbar,
  toolbarOptions,
  toolbarActions,
  body,
  views,
  defaultViewId,
  viewId,
  onViewChange,
  toolbarSlots,
  emptyState,
  loading,
  loadingState,
  error,
  onRowClick,
  onRowContextMenu,
  onBodyContextMenu,
  rowDraggable,
  onRowDragStart,
  rowStyle,
  renderBeforeRow,
  virtualization,
  withCard = true,
  minWidth = 720,
  maxHeight = "100%",
  hideHeaderControls = false,
  scrollbarType = "always",
}: AppDataTableProps<TData>) {
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [striped, setStriped] = useState<boolean | "odd" | "even">(tableProps?.striped ?? true);
  const [compact, setCompact] = useState(tableProps?.verticalSpacing === 0);
  const initialViewId = defaultViewId ?? views?.[0]?.id;
  const [internalViewId, setInternalViewId] = useState(initialViewId);
  const selectedViewId = viewId ?? internalViewId;

  useEffect(() => {
    if (!views?.length || views.some((view) => view.id === selectedViewId)) return;
    setInternalViewId(defaultViewId ?? views[0]!.id);
  }, [defaultViewId, selectedViewId, views]);

  useEffect(() => {
    const viewport = scrollViewportRef.current;
    if (!viewport) return;

    const updateViewportWidth = () => setViewportWidth(viewport.clientWidth || 0);
    updateViewportWidth();

    const observer = new ResizeObserver(updateViewportWidth);
    observer.observe(viewport);

    return () => observer.disconnect();
  }, []);

  const visibleLeafColumns = table.getVisibleLeafColumns();
  const unlockedLeafColumns = visibleLeafColumns.filter((column) => !column.columnDef.meta?.lockWidth);
  const stretchExtra = Math.max(0, viewportWidth - table.getTotalSize());
  const stretchPerUnlocked = unlockedLeafColumns.length > 0 ? stretchExtra / unlockedLeafColumns.length : 0;

  const resolvedLeafColumnWidths = useMemo(() => {
    const widths = new Map<string, number>();
    visibleLeafColumns.forEach((column) => {
      const baseSize = column.getSize();
      const resolvedSize = column.columnDef.meta?.lockWidth ? baseSize : baseSize + stretchPerUnlocked;
      widths.set(column.id, resolvedSize);
    });
    return widths;
  }, [visibleLeafColumns, stretchPerUnlocked]);

  const columnSizeVars = Object.fromEntries(
    table.getFlatHeaders().flatMap((header) => {
      const headerSize = header.column.getLeafColumns()
        .filter((column) => column.getIsVisible())
        .reduce((sum, column) => sum + (resolvedLeafColumnWidths.get(column.id) ?? column.getSize()), 0);

      return [
        [`--header-${header.id}-size`, headerSize],
        [`--col-${header.column.id}-size`, resolvedLeafColumnWidths.get(header.column.id) ?? header.column.getSize()],
      ];
    }),
  ) as React.CSSProperties;

  const rows = table.getRowModel().rows;
  const isVirtualized = virtualization?.enabled === true && !error && !loading && rows.length > 0;
  const getItemKey = useCallback((index: number) => rows[index]?.id ?? index, [rows]);
  const rowVirtualizer = useVirtualizer({
    enabled: isVirtualized,
    // Photo/query updates can overlap scroll events during the initial load.
    // Let React batch these instead of flushing pending mount effects in scroll handlers.
    useFlushSync: false,
    count: rows.length,
    getScrollElement: () => scrollViewportRef.current,
    estimateSize: () => virtualization?.estimateSize ?? 40,
    getItemKey,
    overscan: virtualization?.overscan ?? 12,
  });
  const virtualRows = isVirtualized ? rowVirtualizer.getVirtualItems() : [];
  const virtualPaddingTop = virtualRows.length > 0 ? virtualRows[0]!.start : 0;
  const virtualPaddingBottom = virtualRows.length > 0
    ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1]!.end
    : 0;
  const { headerRef: tableHeaderRef, headerHeight } = useStickyTableHeaderHeight();
  const mergedToolbarOptions = { ...DEFAULT_TOOLBAR_OPTIONS, ...toolbarOptions };
  const showSearch = mergedToolbarOptions.search;
  const showFilter = mergedToolbarOptions.filter;
  const showGrouping = mergedToolbarOptions.grouping;
  const showSort = mergedToolbarOptions.sort;
  const showView = mergedToolbarOptions.view;
  const showMoreOptions = mergedToolbarOptions.moreOptions;
  const showToolbar = Boolean(toolbar) || (views?.length ?? 0) > 1 || showSearch || showFilter || showGrouping || showSort || showView || showMoreOptions;
  const showFooterRowCount = table.options.enableFooterRowCount !== false;
  const hasColumnFooters = table.getFooterGroups().some((footerGroup) =>
    footerGroup.headers.some((header) => !header.isPlaceholder && Boolean(header.column.columnDef.footer)),
  );
  const showFooter = showFooterRowCount || hasColumnFooters;
  const selectedView = views?.find((view) => view.id === selectedViewId) ?? views?.[0];
  const setSelectedView = (nextViewId: string) => {
    if (viewId === undefined) setInternalViewId(nextViewId);
    onViewChange?.(nextViewId);
  };

  const tableElement = (
    <MantineTable.ScrollContainer
        onContextMenu={(event) => {
          if ((event.target as HTMLElement).closest("tbody tr")) return;
          onBodyContextMenu?.(event);
        }}
        className="app-data-table-scroll"
        h="100%"
        minWidth={minWidth}
        maxHeight={maxHeight}
        style={{
          "--app-table-header-height": `${headerHeight}px`,
          flex: 1,
          minHeight: 0,
          //"--app-table-footer-height": `${footerHeight}px`,
          //"--app-table-scrollbar-size": "8px",
        } as React.CSSProperties}
        scrollAreaProps={{
          h: "100%",
          type: scrollbarType,
          offsetScrollbars: true,
          scrollbarSize: 8,
          viewportRef: scrollViewportRef,
          style: { height: "100%", minHeight: 0 },
          classNames: {
            viewport: "app-data-table-scroll-viewport",
            scrollbar: "app-table-scrollbar",
          },
        }}
      >
        <MantineTable
          {...tableProps}
          stickyHeader={tableProps?.stickyHeader ?? true}
          highlightOnHover={tableProps?.highlightOnHover ?? true}
          withColumnBorders={tableProps?.withColumnBorders ?? false}
          striped={striped}
          withRowBorders={tableProps?.withRowBorders ?? true}
          withTableBorder={tableProps?.withTableBorder ?? false}
          verticalSpacing={compact ? 0 : tableProps?.verticalSpacing ?? 4}
          style={{
            width: `max(100%, ${table.getTotalSize()}px)`,
            tableLayout: "fixed",
            ...columnSizeVars,
            ...tableProps?.style,
          }}
          captionSide="bottom"
        >
          <colgroup>
            {table.getVisibleLeafColumns().map((column) => (
              <col key={column.id} style={{ width: `calc(var(--col-${column.id}-size) * 1px)` }} />
            ))}
          </colgroup>
          <MantineTable.Thead ref={tableHeaderRef}>
            {table.getHeaderGroups().map((headerGroup) => (
              <MantineTable.Tr key={headerGroup.id}>
                {headerGroup.headers.filter((header) => header.column.getIsVisible()).map((header) => (
              <AppDataTableHeaderCell key={header.id} header={header} table={table} hideControls={hideHeaderControls} />
                ))}
              </MantineTable.Tr>
            ))}
          </MantineTable.Thead>
          <MantineTable.Tbody>
            {error ? (
              <MantineTable.Tr>
                <MantineTable.Td colSpan={table.getVisibleLeafColumns().length || 1}>{error}</MantineTable.Td>
              </MantineTable.Tr>
            ) : loading ? (
              <MantineTable.Tr>
                <MantineTable.Td colSpan={table.getVisibleLeafColumns().length || 1}>{loadingState ?? "Loading..."}</MantineTable.Td>
              </MantineTable.Tr>
            ) : rows.length === 0 ? (
              <MantineTable.Tr>
                <MantineTable.Td colSpan={table.getVisibleLeafColumns().length || 1}>{emptyState ?? "No records found."}</MantineTable.Td>
              </MantineTable.Tr>
            ) : (
              <>
                {isVirtualized && virtualPaddingTop > 0 ? (
                  <MantineTable.Tr aria-hidden="true">
                    <MantineTable.Td
                      colSpan={table.getVisibleLeafColumns().length || 1}
                      p={0}
                      style={{ height: virtualPaddingTop, border: 0 }}
                    />
                  </MantineTable.Tr>
                ) : null}
                {(isVirtualized ? virtualRows.map((virtualRow) => ({
                  row: rows[virtualRow.index]!,
                  index: virtualRow.index,
                })) : rows.map((row, index) => ({ row, index }))).map(({ row, index }) => {
                  const selected = row.getIsSelected();
                  const resolvedRowStyle = rowStyle?.(row.original);
                  return (
                    <Fragment key={row.id}>
                      {renderBeforeRow?.(row, index, rows)}
                      <MantineTable.Tr
                        className="group"
                        draggable={rowDraggable?.(row.original) ?? false}
                        onDragStart={(event) => {
                          if ((event.target as HTMLElement).closest('[data-row-drag-disabled]')) {
                            event.preventDefault();
                            return;
                          }
                          onRowDragStart?.(event, row.original);
                        }}
                        aria-selected={selected}
                        aria-expanded={row.getCanExpand() ? row.getIsExpanded() : undefined}
                        data-selected={selected || undefined}
                        data-grouped={row.getIsGrouped() || undefined}
                        onClick={(event) => {
                          if (
                            (event.target as HTMLElement).closest(
                              'a, button, input, select, textarea, [role="button"], [role="checkbox"], [role="combobox"], [role="menuitem"], [role="option"], [data-row-click-disabled]',
                            )
                          ) {
                            return;
                          }
                          if (row.getCanExpand()) {
                            row.getToggleExpandedHandler()();
                          } else {
                            onRowClick?.(row.original);
                          }
                        }}
                        onContextMenu={(event) => {
                          if (!onRowContextMenu) return;
                          onRowContextMenu(event, row.original);
                        }}
                        style={{
                          ...resolvedRowStyle,
                          cursor: row.getCanExpand() || onRowClick ? "pointer" : undefined,
                        }}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const isGroupedCell = cell.getIsGrouped();
                          const isPlaceholderCell = cell.getIsPlaceholder();
                          const isTreeRow = row.getVisibleCells().some((visibleCell) => visibleCell.column.columnDef.meta?.tree);
                          const isTreeCell = cell.column.columnDef.meta?.tree === true;

                          return (
                            <MantineTable.Td
                              key={cell.id}
                              align={cell.column.columnDef.meta?.align}
                              className={cell.column.getIsPinned() ? "app-data-table-sticky-cell" : undefined}
                              style={{
                                padding: cell.column.columnDef.meta?.compact ? "2px 4px" : undefined,
                                width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
                                overflow: "hidden",
                                textOverflow: cell.column.columnDef.meta?.truncate ? "ellipsis" : undefined,
                                whiteSpace: cell.column.columnDef.meta?.truncate ? "nowrap" : undefined,
                                ...(!row.getIsGrouped() ? cell.column.columnDef.meta?.conditionalFormatting?.style?.(cell.getValue()) : undefined),
                                ...getPinningStyles(cell.column, selected),
                              }}
                            >
                              {isTreeCell && row.getCanExpand() ? (
                                <Group gap={6} wrap="nowrap" pl={row.depth * 12}>
                                  <ActionIcon
                                    variant="subtle"
                                    size="xs"
                                    aria-label={row.getIsExpanded() ? "Collapse row" : "Expand row"}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      row.getToggleExpandedHandler()();
                                    }}
                                  >
                                    {row.getIsExpanded() ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                                  </ActionIcon>
                                  <Box style={{ minWidth: 0, flex: 1 }}>{cell.column.columnDef.meta?.treeCell?.(row.original) ?? renderCell(table, cell)}</Box>
                                </Group>
                              ) : isGroupedCell && !isTreeRow ? (
                                <Group gap={6} wrap="nowrap" pl={row.depth * 12}>
                                  <ActionIcon
                                    variant="subtle"
                                    size="xs"
                                    aria-label={row.getIsExpanded() ? "Collapse group" : "Expand group"}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      row.getToggleExpandedHandler()();
                                    }}
                                  >
                                    {row.getIsExpanded() ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                                  </ActionIcon>
                                  <Text size="sm" fw={600} truncate>{getGroupedRowLabel(cell)}</Text>
                                  <Text size="xs" c="dimmed" textWrap="nowrap">
                                    {getGroupedRowCount(row).toLocaleString()}
                                  </Text>
                                </Group>
                              ) : isPlaceholderCell && !isTreeRow ? null : row.getCanExpand() && cell.id === row.getVisibleCells()[0]?.id ? (
                                <Group gap={6} wrap="nowrap" pl={row.depth * 12}>
                                  <ActionIcon
                                    variant="subtle"
                                    size="xs"
                                    aria-label={row.getIsExpanded() ? "Collapse row" : "Expand row"}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      row.getToggleExpandedHandler()();
                                    }}
                                  >
                                    {row.getIsExpanded() ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                                  </ActionIcon>
                                  <Box style={{ minWidth: 0, flex: 1 }}>{renderCell(table, cell)}</Box>
                                </Group>
                              ) : (
                                <Box pl={row.depth > 0 && cell.id === row.getVisibleCells()[0]?.id ? row.depth * 12 + 26 : 0}>{renderCell(table, cell)}</Box>
                              )}
                            </MantineTable.Td>
                          );
                        })}
                      </MantineTable.Tr>
                    </Fragment>
                  );
                })}
                {isVirtualized && virtualPaddingBottom > 0 ? (
                  <MantineTable.Tr aria-hidden="true">
                    <MantineTable.Td
                      colSpan={table.getVisibleLeafColumns().length || 1}
                      p={0}
                      style={{ height: virtualPaddingBottom, border: 0 }}
                    />
                  </MantineTable.Tr>
                ) : null}
              </>
            )}
          </MantineTable.Tbody>
        </MantineTable>
      </MantineTable.ScrollContainer>
  );

  const selectedBody = selectedView?.render
    ? selectedView.render({ table, rows })
    : body ?? tableElement;

  return (
    <Stack gap={gap} h="100%" mih={0} miw={0} w="100%" className="app-data-table overflow-hidden" data-table-key={tableKey}>
      {showToolbar ? (
        <Group justify="space-between" gap={gap} p={toolbarPadding} className="app-data-table-toolbar">
          <Group gap={gap} wrap="wrap">
            {toolbarSlots?.start ?? toolbar}
            {showSearch ? (
              <TextInput
                value={table.state.globalFilter ?? ""}
                onChange={(event) => table.setGlobalFilter(event.currentTarget.value)}
                placeholder="Search..."
                leftSection={<IconSearch size={16} />}
                size="xs"
                w={{ base: toolbarOptions?.searchWidth ?? "100%", sm: toolbarOptions?.searchWidth ?? 130 }}
              />
            ) : null}
          </Group>
          {toolbarSlots?.middle}
          {(toolbarSlots?.end || toolbarActions || (views?.length ?? 0) > 1 || showFilter || showGrouping || showSort || showView || showMoreOptions) ? (
            <Group gap={gap} wrap="wrap">
              {toolbarSlots?.end ?? toolbarActions}
              {(views?.length ?? 0) > 1 ? (
                <SegmentedControl
                  size="xs"
                  aria-label="Table view"
                  value={selectedView?.id}
                  onChange={setSelectedView}
                  data={views!.map((view) => ({
                    value: view.id,
                    label: (
                      <Group gap={5} wrap="nowrap">
                        {view.icon}
                        <span>{view.label}</span>
                      </Group>
                    ),
                  }))}
                />
              ) : null}
              <AppTableToolbarControls
                table={table}
                filter={showFilter}
                grouping={showGrouping}
                sort={showSort}
                view={showView}
                moreOptions={showMoreOptions}
                gap={gap}
                striped={striped}
                onStripedChange={setStriped}
                compact={compact}
                onCompactChange={setCompact}
                onResetToDefault={() => {
                  setStriped(tableProps?.striped ?? true);
                  setCompact(tableProps?.verticalSpacing === 0);
                }}
              />
            </Group>
          ) : null}
        </Group>
      ) : null}
      {withCard ? (
        <Paper withBorder h="100%" mih={0} className="overflow-hidden">
            <Stack gap={0} h="100%" mih={0}>
                <Box h="100%" mih={0} className="app-data-table-card">
                    {selectedBody}
                </Box>
                {showFooter ? (
                    <Box>
                        <Divider />
                        <Box>
                            <Text size="xs" c="dimmed" py={3} px={9}>{table.getFooterRowCountLabel()}</Text>
                        </Box>
                    </Box>
                ) : null}
            </Stack>
        </Paper>
      ) : (
        selectedBody
      )}
    </Stack>
  );
}
